/**
 * Dashboard main column: performance trends + recent simulation history (same data path as Results).
 *
 * @module DashboardInsightsPanel
 */

import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { Award, Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { LatestModuleProgressSummary } from "@/components/results/LatestModuleProgressSummary";
import { SimulationPerformanceTrendsSection } from "@/components/results/SimulationPerformanceTrendsSection";
import { SimulationResultCard } from "@/components/results/SimulationResultCard";
import { useAuth } from "@/hooks/useAuth";
import { useDashboardTrendsLevelFilter } from "@/hooks/useDashboardTrendsLevelFilter";
import { useSimulationResults } from "@/hooks/useSimulationResults";
import {
  calculateAverageScores,
  calculateLatestModuleProgress,
  getAttemptedModuleKeys,
  getLatestReviewTargetByModule,
  processChartData,
  type SimulationModuleKey,
} from "@/lib/simulationResultsChartData";
import { ROUTES } from "@/lib/routeConstants";
import { useTranslation } from "react-i18next";
import { useNavigateToModuleReview } from "@/hooks/useNavigateToModuleReview";

const DASHBOARD_HISTORY_LIMIT = 5;

export function DashboardInsightsPanel() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigateToModuleReview = useNavigateToModuleReview({
    returnBasePath: ROUTES.DASHBOARD,
    mode: "dashboard",
  });
  const {
    results: filteredResults,
    isLoading,
    error,
    retryAnalysis,
  } = useSimulationResults("all", "all");

  const {
    availableLevels,
    selectedLevel,
    setLevel,
    filteredResults: trendsResults,
    showLevelFilter,
  } = useDashboardTrendsLevelFilter(filteredResults, user?.id);

  const averageScores = useMemo(
    () => calculateAverageScores(trendsResults),
    [trendsResults],
  );
  const chartData = useMemo(
    () => processChartData(trendsResults),
    [trendsResults],
  );
  const attemptedModules = useMemo(
    () => getAttemptedModuleKeys(trendsResults),
    [trendsResults],
  );
  const latestModuleProgress = useMemo(
    () => calculateLatestModuleProgress(trendsResults),
    [trendsResults],
  );
  const latestReviewByModule = useMemo(
    () => getLatestReviewTargetByModule(trendsResults),
    [trendsResults],
  );

  const handleModuleReviewClick = useCallback(
    (sessionId: string, module: SimulationModuleKey) => {
      navigateToModuleReview(sessionId, module);
    },
    [navigateToModuleReview],
  );

  const handleModuleRetry = useCallback(
    async (sessionId: string, module: string) => {
      try {
        await retryAnalysis(sessionId, module);
      } catch (retryError) {
        console.error("Failed to retry module analysis:", retryError);
      }
    },
    [retryAnalysis],
  );

  const recentResults = useMemo(
    () => filteredResults.slice(0, DASHBOARD_HISTORY_LIMIT),
    [filteredResults],
  );

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-border py-20">
        <Loader2 className="mb-4 h-10 w-10 animate-spin text-primary" />
        <p className="animate-pulse text-muted-foreground">
          {t("common.loading")}
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center rounded-xl border border-destructive/30 bg-destructive/5 py-12 text-center">
        <div className="mb-4 rounded-full bg-destructive/10 p-4">
          <AlertCircle className="h-10 w-10 text-destructive" />
        </div>
        <h2 className="mb-2 text-lg font-semibold">
          {t("simulation.results.errors.unableToLoad")}
        </h2>
        <p className="mb-6 max-w-md text-muted-foreground">{error.message}</p>
        <Button onClick={() => globalThis.location.reload()}>
          {t("simulation.results.errors.tryAgain")}
        </Button>
      </div>
    );
  }

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <div>
        <h2 className="font-ui text-lg font-semibold text-foreground">
          {t("dashboard.insights.snapshotTitle", {
            defaultValue: "Your progress snapshot",
          })}
        </h2>
        <p className="font-ui mt-1 text-sm text-foreground/80">
          {t("dashboard.insights.snapshotSubtitle", {
            defaultValue:
              "Performance trends and recent simulations—same data as Analysis & results, optimized for a quick overview.",
          })}
        </p>
      </div>

      <LatestModuleProgressSummary
        items={latestModuleProgress}
        onModuleClick={handleModuleReviewClick}
      />

      <SimulationPerformanceTrendsSection
        chartData={chartData}
        attemptedModules={attemptedModules}
        averageScores={averageScores}
        latestReviewByModule={latestReviewByModule}
        onModuleReviewClick={handleModuleReviewClick}
        gradientIdPrefix="dashboard"
        moduleChartHeightClassName="h-[160px]"
        showPerModuleCharts={false}
        lineChartHeightClassName="h-[220px]"
        levelFilter={
          showLevelFilter && selectedLevel
            ? {
                availableLevels,
                selectedLevel,
                onLevelChange: setLevel,
              }
            : undefined
        }
      />

      <Card>
        <CardHeader className="flex flex-col gap-2 p-4 sm:flex-row sm:items-center sm:justify-between sm:p-6">
          <div>
            <CardTitle>
              {t("simulation.results.history.title", {
                defaultValue: "Simulation History",
              })}
            </CardTitle>
            <CardDescription>
              {t("dashboard.insights.recentSimulations", {
                count: recentResults.length,
                total: filteredResults.length,
                defaultValue:
                  "Showing {{count}} of {{total}} recent simulations",
              })}
            </CardDescription>
          </div>
          <Button
            variant="outline"
            size="sm"
            asChild
            className="w-full shrink-0 sm:w-auto"
          >
            <Link to={ROUTES.RESULTS}>
              {t("dashboard.insights.viewAll", { defaultValue: "View all" })}
            </Link>
          </Button>
        </CardHeader>
        <CardContent className="p-4 pt-0 sm:p-6 sm:pt-0">
          {filteredResults.length === 0 ? (
            <div className="flex flex-col items-center py-10 text-center">
              <Award
                className="mb-4 h-12 w-12 text-muted-foreground opacity-60"
                aria-hidden
              />
              <p className="font-medium text-foreground">
                {t("dashboard.insights.historyEmptyTitle", {
                  defaultValue: "No simulations yet",
                })}
              </p>
              <p className="mt-2 max-w-md text-sm text-muted-foreground">
                {t("dashboard.insights.historyEmptyDescription", {
                  defaultValue:
                    "Complete an exam simulation to see your results and trends here.",
                })}
              </p>
              <Button asChild className="mt-6">
                <Link to={ROUTES.EXAM}>
                  {t("dashboard.insights.chartEmptyCta", {
                    defaultValue: "Start exam simulation",
                  })}
                </Link>
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {recentResults.map((result) => (
                <SimulationResultCard
                  key={result.id}
                  variant="compact"
                  result={result}
                  onModuleClick={(module) =>
                    navigateToModuleReview(result.id, module)
                  }
                  onModuleRetry={(module) =>
                    handleModuleRetry(result.id, module)
                  }
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
